/**
 * Theme controller — flips `data-theme` on `<html>` and persists to
 * localStorage. The token CSS in `styles/tokens.css` keys off the
 * attribute, so all components flip in lockstep.
 *
 * The theme picker (founder ask 2026-04-30, PROJECT_BOARD §6.4) ships
 * ten options:
 *
 *   - still-water (default since #2496, 16 Oct 2026: the public site's
 *     paper and one teal, for both apps)
 *   - still-water-night (the hero's night sky, mint as the fill)
 *   - sage-clinic (the default until #2496 — picked by founder 2026-04-30)
 *   - linen, periwinkle, sand-slate, dark, harbour, graphite, pine
 *
 * The four pastels come from the research doc
 * `docs/design/ui-theme-refresh-research.md` §3 verbatim. The dark
 * theme is retained so existing users (and the user who balks at the
 * pastel rollout) still have somewhere to land.
 *
 * SSR-safe: every read guards on `typeof window` so the module can be
 * imported from a renderer that pre-runs on Node.
 */

export type Theme =
  | "still-water"
  | "still-water-night"
  | "sage-clinic"
  | "linen"
  | "periwinkle"
  | "sand-slate"
  | "dark"
  | "harbour"
  | "graphite"
  | "pine";

export const THEMES: ReadonlyArray<{
  /** The `data-theme` attribute value + localStorage value. */
  id: Theme;
  /** Name surfaced in the picker. Quiet + professional voice. */
  label: string;
  /** Short note rendered below the label so the picker reads as
   *  considered, not gamified. */
  description: string;
  /** Three-swatch preview shown in the picker. CSS hex strings — these
   *  are the only place hex values are allowed in the SPA, because the
   *  preview must render the theme's bg/accent/accent-fg without
   *  applying the theme to the surrounding chrome. */
  swatches: { bg: string; accent: string; accentFg: string };
}> = [
  {
    id: "still-water",
    label: "Still water",
    description: "Pale paper, one teal accent.",
    swatches: { bg: "#F7FAF8", accent: "#315A60", accentFg: "#315A60" },
  },
  {
    id: "still-water-night",
    label: "Still water night",
    description: "Deep teal night, mint accent.",
    swatches: { bg: "#1B3A45", accent: "#A4C8BA", accentFg: "#B9DACD" },
  },
  {
    id: "harbour",
    label: "Harbour (rebrand preview)",
    description: "Institutional slate-blue, warm clay.",
    swatches: { bg: "#EEF1F6", accent: "#2D5BA8", accentFg: "#1E417C" },
  },
  {
    id: "sage-clinic",
    label: "Sage Clinic",
    description: "Mint paper, deep teal accent.",
    swatches: { bg: "#EEF6F2", accent: "#1F7A6E", accentFg: "#125449" },
  },
  {
    id: "linen",
    label: "Linen",
    description: "Warm linen, sage accent.",
    swatches: { bg: "#F8F4ED", accent: "#5F8C6A", accentFg: "#2F5A3B" },
  },
  {
    id: "periwinkle",
    label: "Periwinkle",
    description: "Soft blue-violet, indigo accent.",
    swatches: { bg: "#F2F1FA", accent: "#5947B8", accentFg: "#3D2D8E" },
  },
  {
    id: "sand-slate",
    label: "Sand & Slate",
    description: "Warm sand, terracotta accent.",
    swatches: { bg: "#F5EFE6", accent: "#A8442A", accentFg: "#7A2E1A" },
  },
  {
    id: "dark",
    label: "Slate Dark",
    description: "Deep slate, indigo accent.",
    swatches: { bg: "#020617", accent: "#6366F1", accentFg: "#A5B4FC" },
  },
  {
    id: "graphite",
    label: "Graphite",
    description: "Warm neutral, bronze accent.",
    swatches: { bg: "#F4F2EF", accent: "#8A5524", accentFg: "#6A3F18" },
  },
  {
    id: "pine",
    label: "Pine",
    description: "Deep evergreen, teal accent.",
    swatches: { bg: "#0E1A18", accent: "#45B3A2", accentFg: "#79D9C8" },
  },
];

const VALID_THEMES: ReadonlySet<Theme> = new Set(THEMES.map((t) => t.id));

/** Sage Clinic was the founder's pick on 2026-04-30; Still water replaced it
 *  as the default for both apps on 2026-10-16 (#2496). */
export const DEFAULT_THEME: Theme = "still-water";

const STORAGE_KEY = "tarii.theme";
// Backwards-compat: prior installs persisted under `roster.theme`. We
// migrate the value forward on first read so existing demo personas
// don't lose their preference across the brand sweep.
const LEGACY_STORAGE_KEY = "roster.theme";
// #2496 — set once `initTheme()` has run on this browser. Until then a stored
// `sage-clinic` is the old default, not a choice, and moves to the new one.
const STILL_WATER_MIGRATED_KEY = "tarii.theme.still-water-migrated";

type Listener = (theme: Theme) => void;
const listeners = new Set<Listener>();

function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && VALID_THEMES.has(value as Theme);
}

function migrateLegacyValue(raw: string): Theme | null {
  // Pre-multi-theme installs only persisted "light" or "dark". Map them
  // to the new picker so the old toggle stays consistent.
  if (raw === "dark") return "dark";
  if (raw === "light") return DEFAULT_THEME;
  if (isTheme(raw)) return raw;
  return null;
}

function readStored(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw !== null) {
      const migrated = migrateLegacyValue(raw);
      if (migrated && migrated !== raw) {
        window.localStorage.setItem(STORAGE_KEY, migrated);
      }
      return migrated;
    }
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy !== null) {
      const migrated = migrateLegacyValue(legacy);
      if (migrated) {
        window.localStorage.setItem(STORAGE_KEY, migrated);
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
        return migrated;
      }
    }
    return null;
  } catch {
    // localStorage may throw in private-mode Safari etc.
    return null;
  }
}

export function getTheme(): Theme {
  return readStored() ?? DEFAULT_THEME;
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
}

export function setTheme(theme: Theme): void {
  if (!isTheme(theme)) return;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistence is best-effort.
    }
  }
  applyTheme(theme);
  for (const fn of listeners) fn(theme);
}

/** Cycle through the five themes. Retained so the dropdown menu item in
 *  the user pill keeps a "next theme" affordance for keyboard parity. */
export function toggleTheme(): Theme {
  const current = getTheme();
  const idx = THEMES.findIndex((t) => t.id === current);
  // `THEMES` is a non-empty constant; `next` is always defined. The
  // assertion narrows the type-checker without expanding runtime risk.
  const next = THEMES[(idx + 1) % THEMES.length] as (typeof THEMES)[number];
  setTheme(next.id);
  return next.id;
}

export function subscribeTheme(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Sage Clinic was the default, so a stored `sage-clinic` rarely means a
 *  choice. Move it to the new default once per browser (#2496); a pick of
 *  Sage Clinic made after that stays. Best-effort: a browser that cannot
 *  write storage just keeps what it has. */
function migrateStoredSageClinic(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(STILL_WATER_MIGRATED_KEY) !== null) return;
    // `readStored()` first: it moves a value held under the old `roster.theme`
    // key into `tarii.theme`, so the check below sees it too.
    if (readStored() === "sage-clinic") {
      window.localStorage.setItem(STORAGE_KEY, DEFAULT_THEME);
    }
    window.localStorage.setItem(STILL_WATER_MIGRATED_KEY, "1");
  } catch {
    // localStorage may throw in private-mode Safari etc.
  }
}

/** Run on app boot to honour the stored preference (or the new default). */
export function initTheme(): void {
  migrateStoredSageClinic();
  applyTheme(getTheme());
}
